/**
 * The three tiles at the top of the Deals home (DOP-615): what needs you,
 * what is running, and what is all clear.
 *
 * Each tile counts exactly the rows whose status says the same thing, because
 * both read `dealStanding`. The "Needs you" tile is the one with a next step,
 * so it is the one that is a button.
 */

import { CircleAlert, CircleCheck, CirclePlay, Loader2 } from "lucide-react";
import React from "react";

import type { DealScreeningDeal } from "../../types/dealScreening";
import { dealStanding } from "./dealStanding";

export interface DealsSummaryTilesProps {
	deals: DealScreeningDeal[];
	/** Opens the list of what needs a person across the deals. */
	onOpenNeedsYou: () => void;
	/**
	 * How many items the Needs you tab lists, once the memos behind it have
	 * loaded, so the tile and the tab say the same number. Until then the
	 * rows' own counts stand in.
	 */
	listedCount?: number | null;
	/** Required sections still to write, by run id (DOP-659), as the table takes them. */
	sectionsToWrite?: Readonly<Record<string, number>>;
}

const names = (deals: DealScreeningDeal[]): string => {
	if (deals.length === 0) return "";
	const shown = deals.slice(0, 2).map((deal) => deal.name);
	const rest = deals.length - shown.length;
	return rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", ");
};

const TILE = "flex min-w-0 flex-col gap-1.5 rounded-xl border px-[18px] py-4 text-left";

const DealsSummaryTiles: React.FC<DealsSummaryTilesProps> = ({
	deals,
	onOpenNeedsYou,
	listedCount = null,
	sectionsToWrite = {},
}) => {
	const standings = deals.map((deal) => ({
		deal,
		standing: dealStanding(
			deal.latest_run,
			deal.latest_run ? (sectionsToWrite[deal.latest_run.public_id] ?? 0) : 0,
		),
	}));
	const needsYou = standings.filter(({ standing }) => standing.kind === "needs_you");
	const openTotal =
		listedCount ?? needsYou.reduce((sum, { standing }) => sum + standing.openCount, 0);
	const writing = standings.filter(({ standing }) => standing.kind === "writing");
	const ready = standings.filter(({ standing }) => standing.kind === "ready");

	return (
		<div className="grid gap-3.5 md:grid-cols-3" aria-label="Deals at a glance" data-tour="deal-tiles">
			<button
				type="button"
				onClick={onOpenNeedsYou}
				disabled={openTotal === 0}
				className={`${TILE} ${
					openTotal > 0
						? "border-[#fedf89] bg-[#fffcf2] hover:border-[#f5c451]"
						: "border-gray-200 bg-white"
				}`}
			>
				<span className="flex items-center gap-2 text-sm font-semibold text-[#b54708]">
					<CircleAlert className="h-4 w-4" aria-hidden />
					Gaps
				</span>
				<span className="text-[28px] font-bold leading-tight text-[#111111]">
					{openTotal === 0 ? "None" : openTotal === 1 ? "1 open" : `${openTotal} open`}
				</span>
				<span className="truncate text-sm text-[#5B6472]">
					{openTotal === 0 ? "No memo is waiting on you" : `On ${names(needsYou.map((s) => s.deal))}`}
				</span>
			</button>
			<div className={`${TILE} border-gray-200 bg-white`}>
				<span className="flex items-center gap-2 text-sm font-semibold text-[#004C4B]">
					{/* A spinner only while something spins: a still one reads as stuck. */}
					{writing.length > 0 ? (
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
					) : (
						<CirclePlay className="h-4 w-4 text-[#9a9ea3]" aria-hidden />
					)}
					Running
				</span>
				<span className="text-[28px] font-bold leading-tight text-[#111111]">
					{writing.length === 1 ? "1 memo" : `${writing.length} memos`}
				</span>
				<span className="truncate text-sm text-[#5B6472]">
					{writing.length === 0 ? "Nothing running" : names(writing.map((s) => s.deal))}
				</span>
			</div>
			<div className={`${TILE} border-gray-200 bg-white`}>
				<span className="flex items-center gap-2 text-sm font-semibold text-[#067647]">
					<CircleCheck className="h-4 w-4" aria-hidden />
					All clear
				</span>
				<span className="text-[28px] font-bold leading-tight text-[#111111]">
					{ready.length === 1 ? "1 memo" : `${ready.length} memos`}
				</span>
				<span className="truncate text-sm text-[#5B6472]">
					{ready.length === 0 ? "None yet" : names(ready.map((s) => s.deal))}
				</span>
			</div>
		</div>
	);
};

export default DealsSummaryTiles;
